/**
 * PATH: packages/email-hub/src/feature/personal/usePersonalData.ts
 * FILE_ID: EMAILHUB-PERSONAL-DATA-001
 * PURPOSE: React-Query data layer for the cross-org "Personal" workspace —
 *          the aggregate inbox, the per-org board mirror, and the private
 *          cross-tag pins (list / create / revoke).
 * WHY:     FEAT-2026-06-22-CROSS-ORG-PERSONAL — operator runs 7 mailboxes
 *          across 3 orgs and wants ONE structured inbox + the ability to pin
 *          an email onto another org's board, with zero cross-org bleed.
 * FLOW:
 *   ┌──────────────┐   ┌───────────────────────┐   ┌────────────────────┐
 *   │ /api/personal│ → │ quotechJson (NO tenant)│ → │ typed query / mut  │
 *   │   /* surfaces│   │ — spans ALL the user's │   │ (inbox / boards /  │
 *   │              │   │   member orgs by       │   │  pins)             │
 *   │              │   │   request.user alone   │   │                    │
 *   └──────────────┘   └───────────────────────┘   └────────────────────┘
 * DEPENDENCIES:
 *   - _internal/services/quotechApi.quotechJson: the cross-master fetch
 *     wrapper. Called WITHOUT `includeTenant`, so NO `?tenant=` is sent —
 *     the Personal endpoints authorise purely from the signed-in user's
 *     OrganisationMember rows and deliberately span every org they belong to.
 *   - @quotech/ui EmailMessage: the row contract the inbox reuses.
 * RELATED:
 *   - PersonalInboxPage.tsx / PersonalBoardsPage.tsx: the consumers.
 *   - backend email_kanban/personal_urls.py: the routes these hit.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query'
import type { BackendSchemas } from '@quotech/api'
import type { EmailMessage } from '@quotech/ui'

import { quotechJson } from '../../_internal/services/quotechApi'
import type { SuggestedOrg } from '../hooks/useEmails'

/** An aggregate-inbox row: the universal email contract + the org it came
 *  from (the FE badges + groups by this). */
export interface PersonalEmail extends EmailMessage {
  source_org_id: number
  source_org_name: string
  present_in_orgs: BackendSchemas['PersonalEmailOrganisationCopy'][]
  suggested_org?: SuggestedOrg | null
}

type PersonalOrganisationStatus =
  BackendSchemas['PersonalOrganisationStatus']
type PersonalOrganisationSetup =
  BackendSchemas['PersonalOrganisationSetup']
export type PersonalOrganisation = PersonalOrganisationSetup['organisation']

/** Cursor-paginated inbox envelope (DRF EmailCursorPagination). */
interface PersonalEmailPage {
  results: PersonalEmail[]
  next: string | null
  previous: string | null
}

/** A board in the read-only mirror (context-free projection). */
export interface PersonalMirrorBoard {
  id: string
  organisation: number
  name: string
  slug: string
  kind: string
  department: string | null
  parent_board: string | null
  visibility: string
  archived: boolean
  display_order: number
}

/** Boards grouped by the org they live in (the mirror's structure). */
export interface PersonalBoardGroup {
  org_id: number
  org_name: string
  boards: PersonalMirrorBoard[]
}

/** A user's private cross-org pin (renders as a card on the target board). */
export interface PersonalPin {
  id: string
  email: string
  email_subject: string
  email_from_address: string
  email_from_name: string | null
  email_received_at: string | null
  email_is_read: boolean
  source_org: number
  source_org_name: string
  target_board: string
  target_board_name: string
  target_org: number
  target_org_name: string
  target_lane: string | null
  note: string
  created_at: string
}

export const PERSONAL_KEYS = {
  organisation: ['personal', 'organisation'] as const,
  emails: ['personal', 'emails'] as const,
  boards: ['personal', 'boards'] as const,
  pins: (boardId?: string) => ['personal', 'pins', boardId ?? 'all'] as const,
}

async function fetchPersonalOrganisationStatus() {
  const { data } = await quotechJson<PersonalOrganisationStatus>(
    '/api/organisations/personal/',
  )
  return data ?? { organisation: null, member_organisation_count: 0 }
}

/** GET setup state only. The read never creates an organisation. */
export function usePersonalOrganisation() {
  return useQuery({
    queryKey: PERSONAL_KEYS.organisation,
    queryFn: fetchPersonalOrganisationStatus,
    select: (status) => status.organisation,
    staleTime: 60_000,
  })
}

/** Lightweight 90/10 guard: multi-org chrome appears only for 2+ memberships. */
export function usePersonalMembershipCount() {
  return useQuery({
    queryKey: PERSONAL_KEYS.organisation,
    queryFn: fetchPersonalOrganisationStatus,
    select: (status) => status.member_organisation_count,
    staleTime: 60_000,
  })
}

/** POST is the explicit opt-in boundary for creating the Personal company. */
export function useSetupPersonalOrganisation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const { data } = await quotechJson<PersonalOrganisationSetup>(
        '/api/organisations/personal/',
        { method: 'POST' },
      )
      if (!data?.organisation) {
        throw new Error('Personal company setup returned no organisation.')
      }
      return data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PERSONAL_KEYS.organisation })
      void qc.invalidateQueries({ queryKey: ['organisations'] })
    },
  })
}

/** GET /api/personal/emails/ — first page of the cross-org aggregate inbox.
 *  v1 surfaces the first cursor page (newest-first); "load more" lands when
 *  the operator asks for volume. NO `?tenant=` → spans all member orgs. */
export function usePersonalEmails(): UseQueryResult<PersonalEmailPage> {
  return useQuery({
    queryKey: PERSONAL_KEYS.emails,
    queryFn: async () => {
      // quotechJson resolves to { response, data } — unwrap the payload.
      const { data } = await quotechJson<PersonalEmailPage>(
        '/api/personal/emails/',
      )
      // Defensive: normalise an empty body to an empty page.
      return data ?? { results: [], next: null, previous: null }
    },
    staleTime: 30_000,
  })
}

/** GET /api/personal/boards/ — the caller's visible boards grouped by org. */
export function usePersonalBoards(): UseQueryResult<PersonalBoardGroup[]> {
  return useQuery({
    queryKey: PERSONAL_KEYS.boards,
    queryFn: async () => {
      const { data } = await quotechJson<{
        organisations: PersonalBoardGroup[]
      }>('/api/personal/boards/')
      return data?.organisations ?? []
    },
    staleTime: 60_000,
  })
}

/** GET /api/personal/pins/ — the caller's active cross-org pins (optionally
 *  narrowed to one board for the mirror overlay). */
export function usePersonalPins(boardId?: string): UseQueryResult<PersonalPin[]> {
  return useQuery({
    queryKey: PERSONAL_KEYS.pins(boardId),
    queryFn: async () => {
      const suffix = boardId ? `?board_id=${encodeURIComponent(boardId)}` : ''
      const { data } = await quotechJson<{ pins: PersonalPin[] }>(
        `/api/personal/pins/${suffix}`,
      )
      return data?.pins ?? []
    },
    staleTime: 30_000,
  })
}

export interface CreatePinInput {
  email_id: string
  target_board_id: string
  target_lane_id?: string | null
  note?: string
}

/** POST /api/personal/pins/ — cross-tag an email onto another org's board.
 *  Invalidates every pin list so the new pin shows on the inbox + mirror. */
export function useCreatePin() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: CreatePinInput) => {
      const { data } = await quotechJson<PersonalPin>('/api/personal/pins/', {
        method: 'POST',
        body: input as unknown as Record<string, unknown>,
      })
      return data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['personal', 'pins'] })
    },
  })
}

/** DELETE /api/personal/pins/<id>/ — soft-revoke one of the caller's pins. */
export function useRevokePin() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (pinId: string) => {
      await quotechJson<unknown>(`/api/personal/pins/${pinId}/`, {
        method: 'DELETE',
      })
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['personal', 'pins'] })
    },
  })
}
